import React, { useState } from 'react';
import { Card, Accordion, Row, Col, Nav, Tab } from 'react-bootstrap';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './CodeDocumentation.css'; // Import CSS file

const CodeDocumentation = () => {
    const [key, setKey] = useState('overview');

    // Process phased image documentation data
    const processDocData = {
        overview: {
            title: "Overview",
            content: "The process_phased_image function is the main controller function for image processing in the Dark Patterns Detection (DD) application server. It processes images in sequential phases, updating Firebase Firestore after each step and maintaining the processing state."
        },
        fileStructure: {
            title: "File Structure",
            content: 
`/DD-application-server
│
├── app.py             # Main application file containing process_phased_image and related functions
│
└── utility/           # Utility directory
    ├── ChromeExtension.py      # Contains upload_image_to_cloud and take_screenshot functions
    ├── OutcomeCordinates.py    # Contains generate_dummy_pointers function
    ├── OutcomeSynthesis.py     # Contains functions for outcome synthesis
    ├── SubAgents.py            # Contains all agent functions
    ├── context.py              # Contains context extraction functions
    ├── LlmCalls.py             # Contains unified API for LLM model calls
    ├── parentCategoryWithScanner.py          # Contains parent category scanner functions
    ├── parentCategoryWithScannerContext.py   # Contains parent category scanner context functions
    ├── RegulatoryPrevention.py               # Contains regulatory prevention functions
    └── XaiDetails.py                         # Contains XAI detail extraction functions`
        },
        endpoints: {
            title: "Associated Endpoints",
            items: [
                {
                    title: "/process_screen_capture (POST)",
                    content: "Triggered by a Cloud Task to process screen capture data. Extracts session_id, journey_id, image_doc_id, public_url, url, and note from the request payload and calls process_phased_image to process the image."
                },
                {
                    title: "/upload_and_process_for_aiagents (POST)",
                    content: "Handles image file upload or image URL, stores in Google Cloud Storage, and processes the image. Validates required parameters, generates unique filename, and processes using process_phased_image."
                },
                {
                    title: "/get_image_metadata (POST)",
                    content: "Gets metadata for an image by calling process_phased_image with the provided parameters and returns the metadata."
                }
            ]
        },
        phases: {
            title: "Processing Phases",
            items: [
                {
                    title: "Information Extraction (Step 1)",
                    content: "Extracts context information from the image and webpage to understand what is being analyzed. This serves as the foundation for all subsequent analysis.",
                    details: "The first step is to extract meaningful context from the image, URL, and user notes that will help identify potential dark patterns. It analyzes multiple aspects of the design and content, runs analyses in parallel for efficiency, and creates a rich foundation for subsequent analysis.",
                    process: [
                        "Get the name of the page using get_the_name_of_the_page from OutcomeSynthesis.py",
                        "Filter the user input using filter_the_user_input from OutcomeSynthesis.py",
                        "Extract context information using get_context_information from context.py",
                        "Get scanner context using get_context_for_scanner from context.py",
                        "Combine all context information for agent functions"
                    ],
                    output: [
                        "pageName - Name of the webpage",
                        "website_brief - Brief description of the website",
                        "webpage_brief - Brief description of the webpage",
                        "nature_of_design - Information about design aspects",
                        "typography_and_colour_use - Information about typography and colors",
                        "call_to_action - Information about calls to action",
                        "information_organization - How information is organized",
                        "footnote_or_information - Footnotes or additional information",
                        "scanner - Scanner information",
                        "Scanner_with_context - Scanner with additional context",
                        "context - Combined context information",
                        "prompt_context - Context formatted for prompts",
                        "note - User notes about the image"
                    ],
                    code: `def step1_information_extraction(image_url, url, note, image_id):
    # Get the name of the page
    pageName = get_the_name_of_the_page(url, image_id)
    
    # Filter the user input
    filtered_note = filter_the_user_input(note, image_id)
    
    # Extract context information
    context_info = get_context_information(image_url, url, filtered_note, image_id)
    
    # Get scanner context
    scanner_context = get_context_for_scanner(
        context_info["website_brief"],
        context_info["webpage_brief"],
        context_info["nature_of_design"],
        context_info["typography_and_colour_use"],
        context_info["call_to_action"],
        image_id
    )
    
    # Return all the extracted information
    return {
        "pageName": pageName,
        "website_brief": context_info["website_brief"],
        "webpage_brief": context_info["webpage_brief"],
        "nature_of_design": context_info["nature_of_design"],
        "typography_and_colour_use": context_info["typography_and_colour_use"],
        "call_to_action": context_info["call_to_action"],
        "information_organization": context_info["information_organization"],
        "footnote_or_information": context_info["footnote_or_information"],
        "scanner": context_info["scanner"],
        "Scanner_with_context": scanner_context,
        "context": context_info,
        "prompt_context": generate_prompt_context(context_info),
        "note": filtered_note
    }`
                },
                {
                    title: "Agents Search (Step 2)",
                    content: "After extracting context information, we determine which specific dark pattern detection agents should be run based on initial parent category classification.",
                    details: "This step runs parent category functions to identify potential dark patterns, using both basic scanner information and scanner with added context. It uses ThreadPoolExecutor to run all analyses in parallel for efficiency.",
                    process: [
                        "Run parent category scanner functions in parallel using ThreadPoolExecutor",
                        "Run parent category scanner context functions in parallel",
                        "Combine both response lists and remove duplicates using set operations",
                        "By running both basic scanner and context-enriched scanner analyses, we increase the chance of detecting patterns (improves recall) and cross-validate findings between approaches (improves precision)"
                    ],
                    output: [
                        "parent_responses_for_scanner - Results from scanner functions",
                        "parent_responses_for_scanner_context - Results from scanner context functions",
                        "combined_responses - Combined unique results from both sets of functions"
                    ],
                    code: `def step2_agents_search(step1_data, image_url, image_id):
    # Run parent category scanner functions in parallel
    with ThreadPoolExecutor() as executor:
        futures = [
            executor.submit(generate_coercing_scanner, image_url, step1_data["scanner"], image_id),
            executor.submit(generate_falsification_scanner, image_url, step1_data["scanner"], image_id),
            executor.submit(generate_misdirection_scanner, image_url, step1_data["scanner"], image_id),
            executor.submit(generate_obstructing_scanner, image_url, step1_data["scanner"], image_id),
            executor.submit(generate_sneaking_scanner, image_url, step1_data["scanner"], image_id),
            executor.submit(generate_omiting_scanner, image_url, step1_data["scanner"], image_id)
        ]
        parent_responses_for_scanner = [future.result() for future in futures]

    # Run parent category scanner context functions in parallel
    with ThreadPoolExecutor() as executor:
        futures = [
            executor.submit(generate_coercing_scanner_context, image_url, step1_data["Scanner_with_context"], image_id),
            executor.submit(generate_falsification_scanner_context, image_url, step1_data["Scanner_with_context"], image_id),
            executor.submit(generate_misdirection_scanner_context, image_url, step1_data["Scanner_with_context"], image_id),
            executor.submit(generate_obstructing_scanner_context, image_url, step1_data["Scanner_with_context"], image_id),
            executor.submit(generate_sneaking_scanner_context, image_url, step1_data["Scanner_with_context"], image_id),
            executor.submit(generate_omiting_scanner_context, image_url, step1_data["Scanner_with_context"], image_id)
        ]
        parent_responses_for_scanner_context = [future.result() for future in futures]

    # Combine both response lists and remove duplicates
    combined_responses = list(set(parent_responses_for_scanner + parent_responses_for_scanner_context))

    return {
        "parent_responses_for_scanner": parent_responses_for_scanner,
        "parent_responses_for_scanner_context": parent_responses_for_scanner_context,
        "combined_responses": combined_responses
    }`
                },
                {
                    title: "Agents Run (Step 3)",
                    content: "Based on the identified parent categories, we run more specific agent functions to analyze detailed dark patterns.",
                    details: "This step runs specialized agent functions to detect and analyze specific dark patterns identified in Step 2. It creates a comprehensive dictionary mapping 35+ agent names to their corresponding functions and uses ThreadPoolExecutor to run them in parallel.",
                    process: [
                        "Create a mapping of agent names to their respective functions from SubAgents.py",
                        "Run each identified agent function in parallel using ThreadPoolExecutor",
                        "Only runs agents identified in the combined_responses list from Step 2",
                        "Filter the agent responses using filter_for_the_response from OutcomeSynthesis.py to remove false positives and ambiguous findings"
                    ],
                    output: [
                        "agent_responses - Raw responses from all agent functions",
                        "revised_agent_responses - Filtered agent responses"
                    ],
                    code: `def step3_agents_run(step1_data, step2_data, image_url, image_id):
    # Create a mapping of agent names to their respective functions
    agent_functions = {
        "generate_preselection": generate_preselection,
        "generate_hidden_legalese": generate_hidden_legalese,
        "generate_potential_false_social_proof": generate_potential_false_social_proof,
        "generate_potential_false_urgency": generate_potential_false_urgency,
        # Many more agent functions...
    }

    # Run each identified agent function in parallel
    agent_responses = []
    with ThreadPoolExecutor() as executor:
        futures = []
        for agent_name in step2_data["combined_responses"]:
            if agent_name in agent_functions:
                futures.append(
                    executor.submit(
                        agent_functions[agent_name],
                        image_url,
                        step1_data["prompt_context"],
                        image_id
                    )
                )
        agent_responses = [future.result() for future in futures]

    # Filter the agent responses
    revised_agent_responses = filter_for_the_response(
        image_url,
        agent_responses,
        image_id
    )

    return {
        "agent_responses": agent_responses,
        "revised_agent_responses": revised_agent_responses
    }`
                },
                {
                    title: "Outcome Synthesis (Step 4)",
                    content: "With all the agent analyses complete, we synthesize the findings into coherent outcomes and validate them.",
                    details: "This step synthesizes outcomes, generates key highlights, and validates the findings to create the final response. It transforms raw agent findings into actionable insights and structures them for display to users.",
                    process: [
                        "Generate key highlights using generate_key_highlights from OutcomeSynthesis.py",
                        "Generate actual measurable harm using generate_actual_measurable_harm from OutcomeSynthesis.py",
                        "Validate outcomes using validation_outcome from OutcomeSynthesis.py, which validates findings against the image, fact-checks the outcome, and structures the outcomes into categories",
                        "Convert outcomes to response format using convert_outcomes_to_response, transforming the findings into a structured format with coordinates"
                    ],
                    output: [
                        "key_highlights - Key highlights from the analysis",
                        "actual_measurable_harm - Identified actual measurable harm",
                        "initial_outcome - Initial outcome data",
                        "Various outcome fields (outcome, hallucination, doubtful)",
                        "outcome_validation_result - Validation result",
                        "outcome_fact_check_response - Fact check response",
                        "response - Final formatted response"
                    ],
                    code: `def step4_outcome_synthesis(step1_data, step3_data, image_url, url, note, image_id):
    # Generate key highlights
    key_highlights = generate_key_highlights(
        step3_data["revised_agent_responses"],
        image_id
    )

    # Generate actual measurable harm
    actual_measurable_harm = generate_actual_measurable_harm(
        step1_data["website_brief"],
        step1_data["webpage_brief"],
        key_highlights,
        image_id
    )

    # Validate outcomes
    validation_result = validation_outcome(
        image_url,
        actual_measurable_harm,
        note,
        step1_data["call_to_action"],
        image_id
    )

    # Convert outcomes to response format
    response = convert_outcomes_to_response(
        validation_result,
        step1_data,
        actual_measurable_harm,
        key_highlights,
        url,
        image_id
    )

    return {
        "key_highlights": key_highlights,
        "actual_measurable_harm": actual_measurable_harm,
        "initial_outcome": validation_result.get("initial_outcome"),
        "outcome": validation_result.get("outcome"),
        "hallucination": validation_result.get("hallucination"),
        "doubtful": validation_result.get("doubtful"),
        "outcome_validation_result": validation_result,
        "outcome_fact_check_response": validation_result.get("outcome_fact_check_response"),
        "response": response
    }`
                }
            ]
        },
        utilities: {
            title: "Key Utility Files",
            items: [
                {
                    title: "LlmCalls.py",
                    content: "Provides a unified interface for calling LLM models with automatic tracking and logging of usage metrics. Features include task-based API key selection, automatic token counting, cost tracking, and response time monitoring.",
                    keyFunctions: [
                        {
                            name: "call_llm(model_name, prompt, task_name, temperature, track_usage, max_tokens, image_url, image_id)",
                            description: "The central function for all LLM calls, supporting both text and image inputs"
                        },
                        {
                            name: "get_api_key_for_task(task_name)",
                            description: "Selects the appropriate API key based on the task type"
                        },
                        {
                            name: "get_db()",
                            description: "Gets Firestore database instance for logging"
                        }
                    ],
                    context: "This module acts as the communication layer between the application and various LLM models. It uses task-based API key selection to distribute load across multiple OpenAI keys and provides automatic tracking of tokens, costs, and response times."
                },
                {
                    title: "context.py",
                    content: "Extracts contextual information from images and webpages using multithreading. Analyzes website brief, webpage content, design aspects, typography, color usage, call-to-action elements, information organization, and footnotes.",
                    keyFunctions: [
                        {
                            name: "get_context_information(image_path, url, note, image_id)",
                            description: "Main function that orchestrates context extraction using multithreading"
                        },
                        {
                            name: "generate_website_brief(url)",
                            description: "Extracts information about the website"
                        },
                        {
                            name: "generate_webpage_brief(image_path, url, image_id)",
                            description: "Summarizes the webpage content from the image"
                        },
                        {
                            name: "generate_nature_of_design(image_path, url, image_id)",
                            description: "Analyzes the design aspects"
                        },
                        {
                            name: "generate_typography_and_colour_use(image_path, url, image_id)",
                            description: "Analyzes typography and color usage"
                        },
                        {
                            name: "generate_call_to_action(image_path, url, image_id)",
                            description: "Identifies and analyzes call-to-action elements"
                        },
                        {
                            name: "generate_information_organization(image_path, url, image_id)",
                            description: "Analyzes how information is organized"
                        },
                        {
                            name: "generate_footnote_or_information(image_path, url, image_id)",
                            description: "Examines footnotes and additional information"
                        },
                        {
                            name: "generate_scanner(image_path, url, note, image_id)",
                            description: "Generates initial scanner analysis based on user notes"
                        },
                        {
                            name: "get_context_for_scanner(website_brief, webpage_brief, nature_of_design, typography_and_colour_use, call_to_action, image_id)",
                            description: "Combines context info for scanner analysis"
                        }
                    ],
                    context: "This module creates a rich understanding of the webpage by analyzing multiple aspects concurrently. It forms the foundation for all subsequent analyses by providing structured information about design elements, content organization, and user interaction points."
                },
                {
                    title: "parentCategoryWithScanner.py",
                    content: "Identifies potential dark patterns at the parent category level. Interacts with Airtable for latest prompts and agent definitions.",
                    keyFunctions: [
                        {
                            name: "get_prompt_for_context_type(function_name)",
                            description: "Fetches appropriate prompts from Airtable"
                        },
                        {
                            name: "get_structure_response(response)",
                            description: "Parses and structures the response"
                        },
                        {
                            name: "get_agents_name_for_prompt_of_parent_category(parent_category)",
                            description: "Retrieves agent names for a parent category"
                        },
                        {
                            name: "summarize_the_prompt(prompt, image_id)",
                            description: "Summarizes agent descriptions"
                        },
                        {
                            name: "generate_coercing_scanner(image_path, scanner, image_id)",
                            description: "Identifies coercing dark patterns that manipulate users into making choices they wouldn't normally make"
                        },
                        {
                            name: "generate_falsification_scanner(image_path, scanner, image_id)",
                            description: "Detects falsification dark patterns that misrepresent information to deceive users"
                        },
                        {
                            name: "generate_misdirection_scanner(image_path, scanner, image_id)",
                            description: "Finds misdirection patterns that distract users from important information"
                        },
                        {
                            name: "generate_obstructing_scanner(image_path, scanner, image_id)",
                            description: "Detects obstructing dark patterns that create impediments for users"
                        },
                        {
                            name: "generate_sneaking_scanner(image_path, scanner, image_id)",
                            description: "Identifies sneaking dark patterns that hide or disguise information"
                        },
                        {
                            name: "generate_omiting_scanner(image_path, scanner, image_id)",
                            description: "Detects omission dark patterns that deliberately leave out key information"
                        }
                    ],
                    context: "This module conducts the high-level analysis of potential dark patterns by classifying them into parent categories. It interacts with Airtable to get the latest prompts and agent definitions, then uses these to analyze the image for specific categories of dark patterns."
                },
                {
                    title: "parentCategoryWithScannerContext.py",
                    content: "Enhanced version of parentCategoryWithScanner.py that incorporates additional context information for more nuanced detection.",
                    keyFunctions: [
                        {
                            name: "generate_coercing_scanner_context(image_path, Scanner_with_context, image_id)",
                            description: "Context-enhanced detection of coercing patterns"
                        },
                        {
                            name: "generate_falsification_scanner_context(image_path, Scanner_with_context, image_id)",
                            description: "Context-enhanced detection of falsification patterns"
                        },
                        {
                            name: "generate_misdirection_scanner_context(image_path, Scanner_with_context, image_id)",
                            description: "Context-enhanced detection of misdirection patterns"
                        },
                        {
                            name: "generate_obstructing_scanner_context(image_path, Scanner_with_context, image_id)",
                            description: "Context-enhanced detection of obstructing patterns"
                        },
                        {
                            name: "generate_sneaking_scanner_context(image_path, Scanner_with_context, image_id)",
                            description: "Context-enhanced detection of sneaking patterns"
                        },
                        {
                            name: "generate_omiting_scanner_context(image_path, Scanner_with_context, image_id)",
                            description: "Context-enhanced detection of omission patterns"
                        }
                    ],
                    context: "This module enhances the parent category analysis by incorporating additional context information. This allows for more nuanced and accurate detection of dark patterns by considering the broader context of the website and page design."
                },
                {
                    title: "SubAgents.py",
                    content: "Contains 35+ specialized agent functions for detecting specific types of dark patterns. Uses a multi-subagent analysis system with progressive analysis stages and a master agent to combine responses.",
                    keyFunctions: [
                        {
                            name: "prompt_filter(prompt, prompt_context, image_id)",
                            description: "Filters and prepares prompts for analysis"
                        },
                        {
                            name: "subagent_1(prompt, image_path, prompt_context, call_to_action, webpage_brief, image_id)",
                            description: "Initial pattern recognition stage"
                        },
                        {
                            name: "subagent_2(prompt, image_path, prompt_context, call_to_action, webpage_brief, image_id)",
                            description: "Situation analysis stage"
                        },
                        {
                            name: "subagent_3(prompt, image_path, prompt_context, call_to_action, webpage_brief, image_id)",
                            description: "Comparative analysis stage"
                        },
                        {
                            name: "subagent_4(prompt, image_path, prompt_context, call_to_action, webpage_brief, image_id)",
                            description: "Critical analysis stage"
                        },
                        {
                            name: "subagent_5(prompt, image_path, prompt_context, call_to_action, webpage_brief, image_id)",
                            description: "Final synthesis stage"
                        },
                        {
                            name: "master_agent_response(prompt, responses, image_path, image_id)",
                            description: "Combines subagent responses and assigns confidence levels"
                        },
                        {
                            name: "generate_response(agent_name, scanner_context, overall_context, parent_category, note, image_path, prompt_context, call_to_action, webpage_brief, image_id)",
                            description: "Generic function used by all specific agent functions to execute the multi-stage analysis pipeline"
                        }
                    ],
                    specificAgents: [
                        "generate_preselection", "generate_hidden_legalese", "generate_potential_false_social_proof", 
                        "generate_potential_false_urgency", "generate_social_proof_through_activity_notifications", 
                        "generate_social_proof_through_testimonial_prompts", "generate_misdirection_with_forced_continuity",
                        "generate_misdirection_with_bait_and_switch", "generate_misdirection_with_visual_interference",
                        "generate_coercion_with_fake_scarcity", "generate_coercion_with_countdown_timers"
                    ],
                    context: "This module represents the core analysis engine, containing specialized agents for detecting specific types of dark patterns. Each agent is focused on a particular deceptive design technique and uses a multi-stage analysis process to verify findings and reduce false positives.",
                    agentStages: [
                        {
                            name: "subagent_1: Initial Pattern Recognition",
                            description: "Analyzes the image for specific patterns related to the dark pattern type and provides basic reasoning"
                        },
                        {
                            name: "subagent_2: Situation Analysis",
                            description: "Analyzes the specific user situation shown in the image and considers the user journey and potential impact"
                        },
                        {
                            name: "subagent_3: Comparative Analysis",
                            description: "Compares findings against examples of the dark pattern and looks for confirmation or disconfirmation"
                        },
                        {
                            name: "subagent_4: Critical Analysis",
                            description: "Applies additional scrutiny to findings and evaluates the strength of evidence"
                        },
                        {
                            name: "subagent_5: Final Synthesis",
                            description: "Combines outputs from all previous subagents and produces a balanced final analysis"
                        },
                        {
                            name: "master_agent_response: Summary and Confidence",
                            description: "Creates a concise, final summary of findings, assigns confidence levels to conclusions, and provides actionable insights about the dark pattern"
                        }
                    ]
                },
                {
                    title: "OutcomeSynthesis.py",
                    content: "Synthesizes agent outputs into coherent outcomes. Includes functions for key highlight generation, measurable harm analysis, outcome validation, and recommendation generation. Supports feedback-based regeneration.",
                    keyFunctions: [
                        {
                            name: "outcomeStructure",
                            description: "Pydantic model defining the structure of outcomes"
                        },
                        {
                            name: "generate_key_highlights(combine_agents_response, image_id)",
                            description: "Extracts key points from agent responses (50-100 words)"
                        },
                        {
                            name: "generate_actual_measurable_harm(website_context, webpage_brief, Key_highlights, image_id)",
                            description: "Analyzes potential harm to users (50-100 words)"
                        },
                        {
                            name: "validation_outcome(image_path, actual_measurable_harm, note, call_to_action, image_id)",
                            description: "Validates findings against the image and performs fact checking"
                        },
                        {
                            name: "filter_for_the_response(image_path, combine_agents_response, image_id)",
                            description: "Filters out false positives and unsupported claims (100-150 words)"
                        },
                        {
                            name: "generate_recommendation(outcome, image_id)",
                            description: "Creates recommendations based on findings"
                        },
                        {
                            name: "regerate_based_on_feedback(image_path, outcome, feedback, image_id)",
                            description: "Refines outcomes based on feedback"
                        },
                        {
                            name: "get_the_name_of_the_page(url, image_id)",
                            description: "Extracts webpage name from URL"
                        },
                        {
                            name: "filter_the_user_input(note, image_id)",
                            description: "Sanitizes and formalizes user notes"
                        }
                    ],
                    context: "This module transforms raw agent findings into actionable insights. It validates, filters, and restructures the information to provide coherent and accurate analysis of dark patterns, including potential harms and recommendations."
                },
                {
                    title: "OutcomeCordinates.py",
                    content: "Generates visual indicators for identified dark patterns on the image, making the analysis more interpretable and actionable.",
                    keyFunctions: [
                        {
                            name: "generate_dummy_pointers(image_url, total_items)",
                            description: "Creates coordinate data for highlighting dark patterns on the image"
                        }
                    ],
                    context: "This module creates the visual feedback mechanism that helps users understand where in the interface the dark patterns are located, making the analysis more interpretable and actionable."
                },
                {
                    title: "RegulatoryPrevention.py",
                    content: "Contains functions for evaluating dark patterns against regulatory frameworks and compliance requirements.",
                    keyFunctions: [
                        {
                            name: "evaluate_regulatory_compliance(outcome, jurisdiction, image_id)",
                            description: "Evaluates if the identified dark patterns violate specific regulatory frameworks"
                        },
                        {
                            name: "generate_regulatory_recommendations(compliance_results, image_id)",
                            description: "Creates recommendations for addressing regulatory concerns"
                        }
                    ],
                    context: "This module helps evaluate the legal and regulatory implications of identified dark patterns, providing insights into potential compliance issues across different jurisdictions."
                },
                {
                    title: "XaiDetails.py",
                    content: "Contains functions for extracting detailed explanations that make the AI analysis transparent and interpretable.",
                    keyFunctions: [
                        {
                            name: "generate_detailed_explanation(dark_pattern_type, evidence, image_id)",
                            description: "Creates detailed explanations of why something was classified as a specific dark pattern"
                        },
                        {
                            name: "generate_visual_evidence_markers(image_url, evidence_points, image_id)",
                            description: "Creates visual indicators pointing to evidence in the image"
                        }
                    ],
                    context: "This module enhances the explainability of the AI system, making it easier for users to understand why specific elements were flagged as dark patterns."
                }
            ]
        },
        dataFlow: {
            title: "Data Flow Diagram",
            content: 
`┌────────────────────┐
│ API Endpoints      │
│ - /process_screen  │
│   _capture         │
│ - /upload_and_     │
│   process_for_     │
│   aiagents         │
│ - /get_image_      │
│   metadata         │
└─────────┬──────────┘
          │
          ▼
┌────────────────────┐
│ process_phased_    │
│ image              │◄───────────┐
│ (Main Controller)  │            │
└─────────┬──────────┘            │
          │                       │
          ▼                       │
┌────────────────────┐            │
│ Step 1:            │            │
│ Information        │            │
│ Extraction         │            │
└─────────┬──────────┘            │
          │                       │
          ▼                       │
┌────────────────────┐            │ Firebase
│ Step 2:            │            │ Updates
│ Agents Search      │            │ Status
└─────────┬──────────┘            │ After
          │                       │ Each
          ▼                       │ Step
┌────────────────────┐            │
│ Step 3:            │            │
│ Agents Run         │            │
└─────────┬──────────┘            │
          │                       │
          ▼                       │
┌────────────────────┐            │
│ Step 4:            │            │
│ Outcome Synthesis  │            │
└─────────┬──────────┘            │
          │                       │
          ▼                       │
┌────────────────────┐            │
│ Firebase Update:   │◄───────────┘
│ status: completed  │
└────────────────────┘`
        },
        mainFunction: {
            title: "Main Controller Function",
            content: "The process_phased_image function orchestrates a sophisticated four-step process for detecting dark patterns in web interface images, updating Firebase Firestore after each successful step.",
            location: "Located in app.py",
            purpose: "The main controller function that processes an image in phases, updating Firebase after each step. It checks the current status and executes the appropriate step.",
            input: [
                "session_id - The session ID",
                "journey_id - The user journey ID", 
                "image_id - The image document ID",
                "image_url - URL of the image to process",
                "url - The webpage URL where the image was captured",
                "note - User notes about the image"
            ],
            output: "Dictionary with the final metadata or current progress state",
            flow: [
                "Get reference to the image document in Firestore",
                "Check current status - initialize if document doesn't exist",
                "Based on status, execute the appropriate processing step:",
                "initial → Run step 1 (Information Extraction)",
                "step1_completed → Run step 2 (Agents Search)",
                "step2_completed → Run step 3 (Agents Run)",
                "step3_completed → Run step 4 (Outcome Synthesis)",
                "Update status after each successful step",
                "Return the current state from Firestore"
            ],
            errorHandling: [
                "Each step is wrapped in a try-except block that:",
                "Catches any exceptions that occur",
                "Logs the error details to Firestore",
                "Preserves the current processing status (does not change it)",
                "Re-raises the exception to exit the process"
            ]
        }
    };

    return (
        <div className="code-documentation-container">
            <h2 className="mb-4">Code Documentation</h2>

            <Tab.Container id="documentation-tabs" activeKey={key} onSelect={(k) => setKey(k)}>
                <Row>
                    <Col md={3}>
                        <Nav variant="pills" className="flex-column mb-4 sticky-top pt-3">
                            <Nav.Item>
                                <Nav.Link eventKey="overview">Overview</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="fileStructure">File Structure</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="mainFunction">Main Controller</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="endpoints">API Endpoints</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="phases">Processing Phases</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="utilities">Key Utility Files</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="dataFlow">Data Flow</Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Col>
                    <Col md={9}>
                        <Tab.Content>
                            <Tab.Pane eventKey="overview">
                                <Card>
                                    <Card.Body>
                                        <Card.Title>{processDocData.overview.title}</Card.Title>
                                        <Card.Text>{processDocData.overview.content}</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>

                            <Tab.Pane eventKey="fileStructure">
                                <Card>
                                    <Card.Body>
                                        <Card.Title>{processDocData.fileStructure.title}</Card.Title>
                                        <div className="file-structure-content">
                                            <SyntaxHighlighter 
                                                language="bash" 
                                                style={vscDarkPlus} 
                                                showLineNumbers={false} 
                                                wrapLines={true}
                                                customStyle={{backgroundColor: '#1e1e1e'}}
                                                codeTagProps={{style: {backgroundColor: '#1e1e1e'}}}
                                            >
                                                {processDocData.fileStructure.content}
                                            </SyntaxHighlighter>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>

                            <Tab.Pane eventKey="mainFunction">
                                <Card>
                                    <Card.Body>
                                        <Card.Title>{processDocData.mainFunction.title}</Card.Title>
                                        <h6>Location: {processDocData.mainFunction.location}</h6>
                                        <p>{processDocData.mainFunction.content}</p>
                                        
                                        <h6 className="mt-4">Purpose:</h6>
                                        <p>{processDocData.mainFunction.purpose}</p>
                                        
                                        <h6 className="mt-4">Input Parameters:</h6>
                                        <ul>
                                            {processDocData.mainFunction.input.map((param, index) => (
                                                <li key={index}>{param}</li>
                                            ))}
                                        </ul>
                                        
                                        <h6 className="mt-4">Output:</h6>
                                        <p>{processDocData.mainFunction.output}</p>
                                        
                                        <h6 className="mt-4">Flow:</h6>
                                        <ol>
                                            {processDocData.mainFunction.flow.map((step, index) => (
                                                <li key={index}>{step}</li>
                                            ))}
                                        </ol>
                                        
                                        <h6 className="mt-4">Error Handling:</h6>
                                        <ul>
                                            {processDocData.mainFunction.errorHandling.map((item, index) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>

                            <Tab.Pane eventKey="endpoints">
                                <Card>
                                    <Card.Body>
                                        <Card.Title>{processDocData.endpoints.title}</Card.Title>
                                        <Accordion defaultActiveKey="0">
                                            {processDocData.endpoints.items.map((endpoint, index) => (
                                                <Accordion.Item eventKey={index.toString()} key={index}>
                                                    <Accordion.Header>{endpoint.title}</Accordion.Header>
                                                    <Accordion.Body>
                                                        <p>{endpoint.content}</p>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            ))}
                                        </Accordion>
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>

                            <Tab.Pane eventKey="phases">
                                <Accordion defaultActiveKey="0">
                                    {processDocData.phases.items.map((phase, index) => (
                                        <Accordion.Item eventKey={index.toString()} key={index}>
                                            <Accordion.Header>{phase.title}</Accordion.Header>
                                            <Accordion.Body>
                                                <p>{phase.content}</p>
                                                <p>{phase.details}</p>
                                                
                                                <h6 className="mt-3">Process:</h6>
                                                <ol>
                                                    {phase.process.map((step, i) => (
                                                        <li key={i}>{step}</li>
                                                    ))}
                                                </ol>
                                                
                                                <h6 className="mt-3">Output:</h6>
                                                <ul>
                                                    {phase.output.map((out, i) => (
                                                        <li key={i}>{out}</li>
                                                    ))}
                                                </ul>
                                                
                                                <h6 className="mt-3">Implementation:</h6>
                                                <SyntaxHighlighter 
                                                    language="python" 
                                                    style={vscDarkPlus} 
                                                    showLineNumbers={false} 
                                                    wrapLines={true}
                                                    customStyle={{backgroundColor: '#1e1e1e'}}
                                                    codeTagProps={{style: {backgroundColor: '#1e1e1e'}}}
                                                >
                                                    {phase.code}
                                                </SyntaxHighlighter>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            </Tab.Pane>

                            <Tab.Pane eventKey="utilities">
                                <Card>
                                    <Card.Body>
                                        <Card.Title>{processDocData.utilities.title}</Card.Title>
                                        <Accordion defaultActiveKey="0">
                                            {processDocData.utilities.items.map((util, index) => (
                                                <Accordion.Item eventKey={index.toString()} key={index}>
                                                    <Accordion.Header>{util.title}</Accordion.Header>
                                                    <Accordion.Body>
                                                        <p>{util.content}</p>
                                                        
                                                        {util.context && (
                                                            <>
                                                                <h6 className="mt-3">Context:</h6>
                                                                <p>{util.context}</p>
                                                            </>
                                                        )}
                                                        
                                                        {util.keyFunctions && util.keyFunctions.length > 0 && (
                                                            <>
                                                                <h6 className="mt-3">Key Functions:</h6>
                                                                <ul className="function-list">
                                                                    {util.keyFunctions.map((func, i) => (
                                                                        <li key={i}>
                                                                            <code>{func.name}</code> - {func.description}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </>
                                                        )}
                                                        
                                                        {util.specificAgents && util.specificAgents.length > 0 && (
                                                            <>
                                                                <h6 className="mt-3">Example Specific Agents:</h6>
                                                                <div className="specific-agents">
                                                                    {util.specificAgents.map((agent, i) => (
                                                                        <span key={i} className="agent-badge">{agent}</span>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )}
                                                        
                                                        {util.agentStages && util.agentStages.length > 0 && (
                                                            <>
                                                                <h6 className="mt-3">Multi-Stage Agent Analysis:</h6>
                                                                <ul className="agent-stages">
                                                                    {util.agentStages.map((stage, i) => (
                                                                        <li key={i}>
                                                                            <strong>{stage.name}</strong> - {stage.description}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </>
                                                        )}
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            ))}
                                        </Accordion>
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>

                            <Tab.Pane eventKey="dataFlow">
                                <Card>
                                    <Card.Body>
                                        <Card.Title>{processDocData.dataFlow.title}</Card.Title>
                                        <div className="data-flow-diagram">
                                            <SyntaxHighlighter 
                                                language="text" 
                                                style={vscDarkPlus} 
                                                showLineNumbers={false} 
                                                wrapLines={true}
                                                customStyle={{backgroundColor: '#1e1e1e'}}
                                                codeTagProps={{style: {backgroundColor: '#1e1e1e'}}}
                                            >
                                                {processDocData.dataFlow.content}
                                            </SyntaxHighlighter>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>
        </div>
    );
};

export default CodeDocumentation; 